import React, { useMemo } from "react";
import { FiBookOpen } from "react-icons/fi";
import LessonCard from "./LessonCard";
import type {
  RecoveryLesson as Lesson,
  RecoveryLessonProgress as LessonProgress,
  RecoveryLessonStatus as LessonStatus,
} from "../../types/recovery";
import { createLogger } from "../../lib/logger";

const log = createLogger("LearnSection");

interface LearnSectionProps {
  weekNumber: number;
  weekIntro: string;
  lessons: Lesson[];
  progress: Record<string, LessonProgress>;
  isLoading: boolean;
  onLessonClick: (lessonId: string) => void;
}

export const LearnSection: React.FC<LearnSectionProps> = React.memo(({
  weekNumber,
  weekIntro,
  lessons,
  progress,
  isLoading,
  onLessonClick,
}) => {
  
  const { sortedLessons, firstIncomplete } = useMemo(() => {
    // Force order to a number during sorting to prevent string-sorting issues (e.g., 10 coming before 2)
    const sorted = [...lessons].sort((a, b) => Number(a.order) - Number(b.order));
    const incomplete = sorted.find(lesson => {
      const status = progress[lesson.id]?.status ?? "not_started";
      return status !== "completed";
    });
    
    log.debug("Recalculating lesson targets", { 
      firstIncompleteId: incomplete?.id 
    });

    return { sortedLessons: sorted, firstIncomplete: incomplete };
  }, [lessons, progress]);

  const getShortIntro = (text: string): string => {
    if (!text) return "";
    const firstSentence = text.split(". ")[0];
    return firstSentence.endsWith(".") ? firstSentence : `${firstSentence}.`;
  };

  return (
    <div className="px-6 mt-7">
      <h2 className="m-0 mb-4 text-2xl font-bold text-brand-dark font-dm-sans tracking-tight">
        Learn about your postnatal journey
      </h2>

      <div className="bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100">
        <div 
          className="min-h-[216px] p-6 pb-8 flex flex-col justify-between"
          style={{ background: "linear-gradient(135deg, #795787 0%, #9A7BA8 100%)" }}
        >
          <div className="w-[56px] h-[56px] rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
            <FiBookOpen size={28} className="text-white" />
          </div>

          <div className="mt-4">
            <span className="text-[10px] font-bold text-white/70 uppercase tracking-[0.2em] font-dm-sans">
              Module {weekNumber}
            </span>
            <h3 className="m-0 mt-1 text-2xl font-bold text-white font-dm-sans leading-tight">
              Physical Wellbeing
            </h3>
            <p className="m-0 mt-3 text-sm font-medium text-white/90 font-dm-sans leading-relaxed max-w-[90%]">
              {getShortIntro(weekIntro)}
            </p>
          </div>
        </div>

        <div className="p-4 sm:p-6 flex flex-col gap-3 bg-gray-50/30">
          {/* ── HIGH QUALITY BRANDED SPINNER ── */}
          {isLoading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 0',
              width: '100%'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid rgba(30, 94, 91, 0.1)',
                borderTop: '4px solid #1e5e5b',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                marginBottom: '16px',
                boxSizing: 'border-box'
              }} />
              <div style={{ fontFamily: 'DM Sans, sans-serif', textAlign: 'center' }}>
                <p style={{
                  color: '#0c2624',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  margin: 0,
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}>
                  Tailoring your journey
                </p>
              </div>
              <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
              `}</style>
            </div>
          ) : sortedLessons.length === 0 ? (
            <div className="py-8 text-center border-2 border-dashed border-gray-100 rounded-2xl">
               <p className="text-sm text-gray-400 font-dm-sans italic">No lessons available for this week.</p>
            </div>
          ) : (
            sortedLessons.map((lesson) => {
              const hasVideo = Boolean(lesson.vimeoId);
              const lessonProgress = progress[lesson.id];
              const status: LessonStatus = lessonProgress?.status ?? "not_started";
              const percentage = lessonProgress?.percentage ?? 0;

              const isCurrentTarget = firstIncomplete?.id === lesson.id;
              
              // FIX: Wrapped order properties in Number() to guarantee the comparison works 
              // even if the database passes them down as string characters.
              const isAbsoluteGraduation = Number(lesson.order) === 15;
              const isLastOfCurrentWeek = Number(lesson.order) === Number(sortedLessons[sortedLessons.length - 1]?.order);

              let labelText = "Recommended for you";
              
              if (Number(lesson.order) === 1 && status === "not_started") {
                labelText = "Start Here";
              } else if (isCurrentTarget && status === "in_progress") {
                labelText = "Continue Learning";
              } else if (isAbsoluteGraduation && status !== "completed") {
                labelText = "Final Milestone";
              } else if (isLastOfCurrentWeek && status !== "completed") {
                labelText = "Week Completion";
              } else if (isCurrentTarget) {
                labelText = "Up Next";
              }

              const durationText = typeof lesson.durationMins === "number"
                ? `${lesson.durationMins} mins`
                : hasVideo ? "Video Lesson" : "Coming soon";

              return (
                <LessonCard
                  key={lesson.id}
                  title={lesson.title}
                  duration={durationText}
                  hasVideo={hasVideo}
                  status={status}
                  percentage={percentage}
                  isRecommended={isCurrentTarget || ((isAbsoluteGraduation || isLastOfCurrentWeek) && status !== "completed" && status !== "not_started")}
                  recommendationText={labelText}
                  onClick={() => onLessonClick(lesson.id)}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
});

LearnSection.displayName = "LearnSection";
export default LearnSection;